import { useState } from 'react';

export function useConfirm() {
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'danger', // danger, warning, info
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    onConfirm: null,
    icon: '⚠️'
  });

  const showConfirm = (options) => {
    const {
      title,
      message,
      type = 'danger',
      confirmText = 'Confirm',
      cancelText = 'Cancel',
      onConfirm,
      icon
    } = options;

    // Set default icons based on type
    const defaultIcons = {
      danger: '🗑️',
      warning: '⚠️',
      info: 'ℹ️',
      success: '✅'
    };

    setConfirmState({
      isOpen: true,
      title,
      message,
      type,
      confirmText,
      cancelText,
      onConfirm,
      icon: icon || defaultIcons[type]
    });
  };

  const handleClose = () => {
    setConfirmState(prev => ({ ...prev, isOpen: false }));
  };

  const handleConfirm = async () => {
    if (confirmState.onConfirm) {
      await confirmState.onConfirm();
    }
    handleClose();
  };

  const ConfirmDialog = () => {
    if (!confirmState.isOpen) return null;

    const getTypeStyles = () => {
      switch (confirmState.type) {
        case 'danger':
          return {
            bg: 'rgba(239, 68, 68, 0.1)',
            color: 'var(--danger-color)',
            border: 'rgba(239, 68, 68, 0.2)',
            confirmBg: 'var(--danger-color)',
            confirmBorder: 'var(--danger-color)'
          };
        case 'warning':
          return {
            bg: 'rgba(245, 158, 11, 0.1)',
            color: '#f59e0b',
            border: 'rgba(245, 158, 11, 0.2)',
            confirmBg: '#f59e0b',
            confirmBorder: '#f59e0b'
          };
        case 'info':
          return {
            bg: 'rgba(99, 102, 241, 0.1)',
            color: 'var(--accent-blue)',
            border: 'rgba(99, 102, 241, 0.2)',
            confirmBg: 'var(--accent-blue)',
            confirmBorder: 'var(--accent-blue)'
          };
        case 'success':
          return {
            bg: 'rgba(34, 197, 94, 0.1)',
            color: 'var(--primary-alt)',
            border: 'rgba(34, 197, 94, 0.2)',
            confirmBg: 'var(--primary-alt)',
            confirmBorder: 'var(--primary-alt)'
          };
        default:
          return {
            bg: 'var(--card-soft-ac)',
            color: 'var(--primary-color)',
            border: 'var(--primary-color)',
            confirmBg: 'var(--primary-color)',
            confirmBorder: 'var(--primary-color)'
          };
      }
    };

    const styles = getTypeStyles();

    return (
      <div 
        className="modal-overlay" 
        onClick={handleClose}
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'var(--overlay-bg)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 15000,
          backdropFilter: 'blur(4px)'
        }}
      >
        <div 
          className="modal-container"
          onClick={e => e.stopPropagation()}
          style={{
            maxWidth: '420px',
            width: '90%',
            background: 'var(--bg-elevated)',
            borderRadius: '16px',
            boxShadow: 'var(--shadow-strong)',
            border: '1px solid var(--border-color)',
            overflow: 'hidden',
            animation: 'modalSlideIn 0.3s ease-out'
          }}
        >
          <div style={{ padding: '32px 24px', textAlign: 'center' }}>
            {/* Icon */}
            <div style={{ 
              width: '72px',
              height: '72px',
              background: styles.bg,
              color: styles.color,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '36px',
              margin: '0 auto 20px',
              border: `2px solid ${styles.border}`,
              boxShadow: `0 4px 12px ${styles.bg}`
            }}>
              {confirmState.icon}
            </div>
            
            {/* Title */}
            <h3 style={{ 
              fontSize: '20px',
              fontWeight: '700',
              marginBottom: '12px',
              color: 'var(--text-color)',
              lineHeight: '1.3'
            }}>
              {confirmState.title}
            </h3>
            
            {/* Message */}
            <p style={{ 
              color: 'var(--muted-text)',
              marginBottom: '28px',
              fontSize: '15px',
              lineHeight: '1.5',
              maxWidth: '320px',
              margin: '0 auto 28px'
            }}>
              {confirmState.message}
            </p>
            
            {/* Buttons */}
            <div style={{ 
              display: 'flex', 
              gap: '12px',
              justifyContent: 'center'
            }}>
              <button 
                className="btn-ghost" 
                onClick={handleClose}
                style={{ 
                  flex: 1,
                  padding: '14px 20px',
                  fontSize: '15px',
                  fontWeight: '600',
                  borderRadius: '10px',
                  color: 'var(--text-color)',
                  border: '1px solid var(--btn-ghost-border)',
                  background: 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = 'var(--bg-color)';
                  e.target.style.borderColor = 'var(--border-color)';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.borderColor = 'var(--btn-ghost-border)';
                }}
              >
                {confirmState.cancelText}
              </button>
              <button 
                className="btn-primary" 
                onClick={handleConfirm}
                style={{ 
                  flex: 1,
                  padding: '14px 20px',
                  fontSize: '15px',
                  fontWeight: '600',
                  borderRadius: '10px',
                  color: 'white',
                  border: `1px solid ${styles.confirmBg}`,
                  background: styles.confirmBg,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: `0 2px 8px ${styles.bg}`
                }}
                onMouseOver={(e) => {
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = `0 4px 12px ${styles.bg}`;
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = `0 2px 8px ${styles.bg}`;
                }}
              >
                {confirmState.confirmText}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return {
    showConfirm,
    ConfirmDialog,
    isOpen: confirmState.isOpen
  };
}

// Convenience functions for common confirmations
export const confirmDanger = (showConfirm, title, message, onConfirm) => {
  showConfirm({
    title,
    message,
    type: 'danger',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    onConfirm
  });
};

export const confirmWarning = (showConfirm, title, message, onConfirm) => {
  showConfirm({
    title,
    message,
    type: 'warning',
    confirmText: 'Continue',
    cancelText: 'Cancel',
    onConfirm
  });
};

export const confirmInfo = (showConfirm, title, message, onConfirm) => {
  showConfirm({
    title,
    message,
    type: 'info',
    confirmText: 'OK',
    cancelText: 'Cancel',
    onConfirm
  });
};
